import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import type { MutableRefObject } from 'react';

gsap.registerPlugin(ScrollTrigger);

export interface ScrollSystem {
  destroy: () => void;
}

/**
 * Lenis + GSAP ScrollTrigger wiring (spec Page 8.1/8.3). Writes the global
 * 0->1 scroll progress into `progressRef.current` every frame — never
 * React state, so the Earth's useFrame loop never triggers a re-render.
 *
 * `prefers-reduced-motion` kills Lenis smoothing entirely (native scroll)
 * per spec Page 8.4; ScrollTrigger still tracks progress so section reveal
 * fades can run (reduced to simple opacity, see motion/reveal.ts).
 */
export function initScrollSystem(
  progressRef: MutableRefObject<number>,
  opts: { reducedMotion: boolean }
): ScrollSystem {
  let lenis: Lenis | null = null;
  let tickerFn: ((time: number) => void) | null = null;

  if (!opts.reducedMotion) {
    lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenis.on('scroll', ScrollTrigger.update);
    tickerFn = (time: number) => lenis?.raf(time * 1000);
    gsap.ticker.add(tickerFn);
    gsap.ticker.lagSmoothing(0);
  }

  const trigger = ScrollTrigger.create({
    trigger: document.body,
    start: 'top top',
    end: 'bottom bottom',
    onUpdate: (self) => {
      progressRef.current = self.progress;
    },
  });

  // Content (Firestore-driven cards, fonts) can change body height after
  // first paint — keep ScrollTrigger's measurements honest.
  const onResize = () => ScrollTrigger.refresh();
  window.addEventListener('resize', onResize);
  const refreshTimer = window.setTimeout(() => ScrollTrigger.refresh(), 300);

  return {
    destroy: () => {
      window.clearTimeout(refreshTimer);
      window.removeEventListener('resize', onResize);
      trigger.kill();
      if (tickerFn) gsap.ticker.remove(tickerFn);
      lenis?.destroy();
    },
  };
}
